import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import IconButton from 'LuxKit/Button/IconButton'
import LineArrowsSwapHorizontalIcon from 'LuxKit/Icons/line/LineArrowsSwapHorizontalIcon'
import { rem } from 'polished'
import { forwardRef, useCallback, useContext, useImperativeHandle, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import CarsAirportTransferLocationInput, { type CarsAirportTransferLocationInputHandle } from './CarsAirportTransferLocationInput'
import Divider from 'LuxKit/Divider'
import CarsAirportTransferDateTimePicker from './CarsAirportTransferDateTimePicker'
import { SearchMenuStates } from 'components/Search/type'
import CarsAirportTransferPaxAndLuggageInputs from './CarsAirportTransferPaxAndLuggageInputs'
import useAirportTransferSearchControls from '../hooks/useAirportTransferSearchControls'
import type { AirportTransferSearchEmptyField, CarsAirportTransferSearchFieldHandle } from '../hooks/useAirportTransferSearchPrompt'

const InputContainer = styled.div`
  display: grid;
  grid-template-columns: 1.5fr auto 1fr auto auto;
  position: relative;
  align-items: center;
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const InputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  position: relative;
  align-items: center;
  height: 100%;

  .input-to {
    padding-inline-start: ${rem(16)};
  }
`

const ExchangeButtonContainer = styled.div`
  position: absolute;
  inset-inline-start: 50%;
  transform: translateX(-50%);
  z-index: 1;

  [dir='rtl'] & {
    transform: translateX(50%);
  }

  > :first-child {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }
`

const FieldsDivider = styled(Divider)`
  margin: ${rem(4)} 0;
  background-color: ${props => props.theme.palette.neutral.surface.lightForeground};

  &.inner-divider {
    margin: ${rem(8)} 0;
  }
`

const CarsAirportTransferInputs = forwardRef<CarsAirportTransferSearchFieldHandle>(function CarsAirportTransferInputs(_props, ref) {
  const intl = useIntl()
  const fromLocationRef = useRef<CarsAirportTransferLocationInputHandle>(null)
  const toLocationRef = useRef<CarsAirportTransferLocationInputHandle>(null)

  const { checkinDate, checkoutDate, pickUpTime, dropOffTime, airportTransferJourneyType, activeMenu } = useContext(GlobalSearchStateContext)

  const {
    closeLocations,
    closePaxAndLuggageSelection,
    closePickUpDatePicker,
    closeReturnDatePicker,
    openLocations,
    openPaxAndLuggageSelection,
    openPickUpDatePicker,
    openReturnDatePicker,
    onFromLocationSelect,
    onPickUpDateChange,
    onPickUpTimeSelect,
    onReturnDateChange,
    onSwapLocations,
    onToLocationSelect,
  } = useAirportTransferSearchControls()

  const openFirstEmptyField = useCallback((field: AirportTransferSearchEmptyField | undefined) => {
    if (field === 'from') {
      fromLocationRef.current?.focus()
      return
    }

    if (field === 'to') {
      toLocationRef.current?.focus()
      return
    }

    if (field === 'pickupDate') {
      openPickUpDatePicker()
      return
    }

    if (field === 'returnDate') {
      openReturnDatePicker()
    }
  }, [openPickUpDatePicker, openReturnDatePicker])

  useImperativeHandle(ref, () => ({
    openFirstEmptyField,
  }), [openFirstEmptyField])

  return (
    <InputContainer>
      <InputGroup>
        <CarsAirportTransferLocationInput
          ref={fromLocationRef}
          placeholder={intl.formatMessage({
            id: 'pkCU2oWgq8iJH4Ocb9Cs',
            defaultMessage: 'From',
            description: 'Origin location placeholder for airport transfer search',
          })}
          onLocationSelect={onFromLocationSelect}
          onLocationsOpen={openLocations}
          onLocationsClose={closeLocations}
        />
        <FieldsDivider kind="primary" direction="vertical" />
        <CarsAirportTransferLocationInput
          ref={toLocationRef}
          placeholder={intl.formatMessage({
            id: 'lN8f7m8Tv_EFvjhc00YU',
            defaultMessage: 'To',
            description: 'Destination location placeholder for airport transfer search',
          })}
          className="input-to"
          isSecondaryLocation
          onLocationSelect={onToLocationSelect}
          onLocationsOpen={openLocations}
          onLocationsClose={closeLocations}
        />
        <ExchangeButtonContainer className="swap">
          <IconButton
            variant="default"
            kind="secondary"
            size="small"
            shape="circle"
            onClick={onSwapLocations}
          >
            <LineArrowsSwapHorizontalIcon size="S" />
          </IconButton>
        </ExchangeButtonContainer>
      </InputGroup>
      <FieldsDivider kind="primary" direction="vertical" />
      <InputGroup>
        <CarsAirportTransferDateTimePicker
          label={intl.formatMessage({
            id: 'xPsHxVceATkv8eQMK10C',
            defaultMessage: 'Add pick-up',
            description: 'Pick-up date action label for airport transfer search',
          })}
          timeInputLabel={intl.formatMessage({
            id: '27PuTIOhkKp0ITt96o5r',
            defaultMessage: 'Pickup time',
            description: 'Label for pickup time input in airport transfer search',
          })}
          selectedDate={checkinDate}
          selectedTime={pickUpTime}
          onChange={onPickUpDateChange}
          onTimeSelect={onPickUpTimeSelect}
          onDatePickerOpen={openPickUpDatePicker}
          onDatePickerClose={closePickUpDatePicker}
          isOpen={activeMenu === SearchMenuStates.AirportTransferPickUpDatePicker}
        />
        <FieldsDivider kind="primary" direction="vertical" className="inner-divider" />
        <CarsAirportTransferDateTimePicker
          label={intl.formatMessage({
            id: 'v8Vp0RJFlYEF0DutgwCO',
            defaultMessage: 'Add return',
            description: 'Return date action label for airport transfer search',
          })}
          timeInputLabel={intl.formatMessage({
            id: 'StDhmROiZlH3VJrU9vb-',
            defaultMessage: 'Return pickup time',
            description: 'Return pick-up time label for airport transfer search',
          })}
          selectedDate={checkoutDate}
          selectedTime={dropOffTime}
          onChange={onReturnDateChange}
          onDatePickerOpen={openReturnDatePicker}
          onDatePickerClose={closeReturnDatePicker}
          isOpen={activeMenu === SearchMenuStates.AirportTransferReturnDatePicker}
          pickUpDate={checkinDate}
          minDate={checkinDate}
          minTime={pickUpTime}
          required={airportTransferJourneyType === 'return'}
        />
      </InputGroup>
      <FieldsDivider kind="primary" direction="vertical" />
      <CarsAirportTransferPaxAndLuggageInputs
        onSelectionOpen={openPaxAndLuggageSelection}
        onSelectionClose={closePaxAndLuggageSelection}
      />
    </InputContainer>
  )
})

export default CarsAirportTransferInputs
